import api from './api'

export interface LoginCredentials {
  email: string
  password: string
}

export interface SignupData {
  email: string
  password: string
  name: string
  phone?: string
}

export interface SelfSignupData {
  // User data
  email: string
  password: string
  name: string
  phone?: string
  // Company data
  companyType: 'shipper' | 'freight-forwarder'
  companyName: string
  tradingName?: string
  countryCode: string
  primaryIdentifier: string
  primaryIdentifierType: string
}

export interface UserProfile {
  id: string
  name: string
  email: string
  phone?: string
  avatar_url?: string
  cargo?: string
  department?: string
  tenant_id?: string
  current_company?: {
    id: string
    type: 'shipper' | 'freight-forwarder'
    name: string
    tenant_id?: string
    logo_url?: string
    actor_id?: string
    is_approved?: boolean
  }
  companies: Array<{
    id: string
    type: 'shipper' | 'freight-forwarder'
    name: string
    trading_name?: string
    document?: string
    role: string
    tenant_id?: string
    logo_url?: string
    actor_id?: string
  }>
  user_master: boolean
  user_tenant_admin: boolean
  onboarding_completed?: boolean
}

/**
 * Sign up com email e password
 */
export async function signup(data: SignupData) {
  const response = await api.post('/auth/signup', data)
  return response.data
}

/**
 * Self-service signup with auto-provisioning
 * Creates user + tenant + company in one transaction
 */
export async function selfSignup(data: SelfSignupData) {
  const response = await api.post('/auth/self-signup', data)

  // If auto-login was successful, store tokens
  if (response.data.access_token) {
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
  }

  return response.data
}

/**
 * Login com email e password
 */
export async function login(credentials: LoginCredentials) {
  const response = await api.post('/auth/login', credentials)

  if (response.data.access_token) {
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
  }

  return response.data
}

/**
 * Enviar magic link
 */
export async function sendMagicLink(email: string) {
  const response = await api.post('/auth/magic-link', { email })
  return response.data
}

/**
 * Verificar OTP do magic link
 */
export async function verifyOTP(email: string, token: string) {
  const response = await api.post('/auth/verify-otp', { email, token, type: 'magiclink' })

  if (response.data.access_token) {
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
  }

  return response.data
}

/**
 * Verificar magic link token (custom flow)
 */
export async function verifyMagicLink(email: string, token: string) {
  const response = await api.post('/auth/verify-magic-link', { email, token })

  if (response.data.access_token) {
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('refresh_token', response.data.refresh_token)
  }

  return response.data
}

/**
 * Enviar email de recuperação de senha
 */
export async function sendPasswordReset(email: string) {
  const response = await api.post('/auth/forgot-password', { email })
  return response.data
}

/**
 * Redefinir senha com token
 */
export async function resetPassword(email: string, token: string, newPassword: string) {
  const response = await api.post('/auth/reset-password', { email, token, newPassword })
  return response.data
}

/**
 * Alterar senha (usuário logado)
 */
export async function changePassword(currentPassword: string, newPassword: string) {
  const response = await api.post('/auth/change-password', { currentPassword, newPassword })
  return response.data
}

/**
 * Logout
 */
export async function logout() {
  try {
    await api.post('/auth/logout')
  } finally {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }
}

/**
 * Obter perfil do usuário atual
 */
export async function getProfile(): Promise<UserProfile> {
  const response = await api.get('/auth/me')
  return response.data
}

/**
 * Atualizar perfil
 */
export async function updateProfile(data: Partial<UserProfile>) {
  const response = await api.patch('/auth/me', data)
  return response.data
}

/**
 * Trocar empresa atual
 */
export async function changeCurrentCompany(companyId: string, companyType: 'shipper' | 'freight-forwarder') {
  const response = await api.post('/auth/change-company', {
    company_id: companyId,
    company_type: companyType,
  })
  return response.data
}

/**
 * Verificar se há token válido
 */
export function hasValidToken(): boolean {
  const token = localStorage.getItem('access_token')
  if (!token) return false

  try {
    // Decodifica o JWT para verificar expiração
    const payload = JSON.parse(atob(token.split('.')[1]))
    const now = Date.now() / 1000
    return payload.exp > now
  } catch {
    return false
  }
}

/**
 * Upload de avatar do usuário
 * Uses fetch directly because api.post does JSON.stringify which breaks FormData
 */
export async function uploadAvatar(file: File): Promise<{ message: string; avatar_url: string }> {
  const formData = new FormData()
  formData.append('avatar', file)

  const token = localStorage.getItem('access_token')
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

  const response = await fetch(`${API_URL}/auth/me/avatar`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    body: formData
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Erro ao fazer upload' }))
    throw { response: { status: response.status, data } }
  }

  return response.json()
}

/**
 * Excluir avatar do usuário
 */
export async function deleteAvatar(): Promise<{ message: string }> {
  const response = await api.delete('/auth/me/avatar')
  return response.data
}

/**
 * Upload de logo da empresa
 * Uses fetch directly because api.post does JSON.stringify which breaks FormData
 */
export async function uploadCompanyLogo(file: File): Promise<{ message: string; logoUrl: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const token = localStorage.getItem('access_token')
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

  const response = await fetch(`${API_URL}/upload/logo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type - browser will set it with boundary for FormData
    },
    body: formData
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Erro ao fazer upload' }))
    throw { response: { status: response.status, data } }
  }

  return response.json()
}

/**
 * Excluir logo da empresa
 */
export async function deleteCompanyLogo(): Promise<{ message: string }> {
  const response = await api.delete('/upload/logo')
  return response.data
}

export interface PendingInvite {
  id: string
  email: string
  token: string
  role: string
  companyId: string
  companyType: 'shipper' | 'freight-forwarder'
  tenantId: string
  expiresAt: string
  createdAt: string
  tenantName?: string
  invitedByName?: string
  companyName: string
}

/**
 * Buscar convites pendentes do usuário logado
 */
export async function getMyInvites(): Promise<{ invites: PendingInvite[] }> {
  const response = await api.get('/auth/my-invites')
  return response.data
}

/**
 * Aceitar um convite
 */
export async function acceptInvite(token: string): Promise<{ message: string; invite: PendingInvite; user: UserProfile }> {
  const response = await api.post('/auth/accept-invite', { token })
  return response.data
}
